/**
 * Assignee Cache Service
 *
 * Caches task assignees (staff + care teams) in IndexedDB with an
 * in-memory layer for instant synchronous access. Scoped by staffId
 * to prevent cross-user data leakage.
 */

const DB_NAME = "MPSAssigneeCache";
const DB_VERSION = 1;
const STORE_NAME = "assignees";

class AssigneeCacheService {
  constructor() {
    this.db = null;
    this.dbReady = false;
    this._cache = null; // in-memory cache: array of assignee objects
    this._cacheStaffId = null; // staffId the cache belongs to
    this.dbPromise = this.initDB();

    this.dbPromise
      .then(() => {
        this.dbReady = true;
      })
      .catch((error) => {
        console.error("Failed to initialize AssigneeCache IndexedDB:", error);
      });
  }

  /**
   * Initialize IndexedDB database
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => {
        console.error("Failed to open IndexedDB for assignee cache");
        reject(request.error);
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, {
            keyPath: "staffId",
          });
        }
      };
    });
  }

  /**
   * Get current staff ID from Redux store
   */
  getCurrentStaffId() {
    try {
      const { store } = require("../State/store/configureStore");
      const state = store.getState();
      return (
        state?.user?.data?.id ||
        state?.user?.data?.staffId ||
        state?.user?.data?.userId ||
        null
      );
    } catch (error) {
      console.warn("Could not get current staff ID:", error);
      return null;
    }
  }

  /**
   * Synchronous read from in-memory cache.
   * Returns the cached assignees array or null.
   */
  getCachedAssignees() {
    const staffId = this.getCurrentStaffId();
    if (!staffId) return null;

    if (this._cache && this._cacheStaffId === staffId) {
      return this._cache;
    }
    return null;
  }

  /**
   * Async read from IndexedDB.
   * Also populates in-memory cache on success.
   */
  async getAssignees() {
    const staffId = this.getCurrentStaffId();
    if (!staffId) return null;

    try {
      if (!this.db) await this.dbPromise;

      return new Promise((resolve) => {
        const transaction = this.db.transaction([STORE_NAME], "readonly");
        const store = transaction.objectStore(STORE_NAME);
        const request = store.get(staffId);

        request.onsuccess = () => {
          const result = request.result;
          if (result && result.assignees && result.assignees.length > 0) {
            this._cache = result.assignees;
            this._cacheStaffId = staffId;
            resolve(result.assignees);
          } else {
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error("Failed to get assignees from IndexedDB");
          resolve(null);
        };
      });
    } catch (error) {
      console.error("Error reading assignee cache:", error);
      return null;
    }
  }

  /**
   * Save assignees to both in-memory cache and IndexedDB.
   */
  async saveAssignees(assignees) {
    const staffId = this.getCurrentStaffId();
    if (!staffId) return;

    // Update in-memory cache immediately
    this._cache = assignees;
    this._cacheStaffId = staffId;

    // Persist to IndexedDB in background
    try {
      if (!this.db) await this.dbPromise;

      const transaction = this.db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);

      store.put({
        staffId,
        assignees,
        updatedAt: new Date().toISOString(),
      });
    } catch (error) {
      console.error("Error saving assignee cache:", error);
    }
  }

  /**
   * Clear all cached data (call on logout).
   */
  async clearCache() {
    this._cache = null;
    this._cacheStaffId = null;

    try {
      if (this.dbReady && this.db) {
        const transaction = this.db.transaction([STORE_NAME], "readwrite");
        const store = transaction.objectStore(STORE_NAME);
        store.clear();
      }
    } catch (error) {
      console.error("Error clearing assignee cache:", error);
    }
  }
}

const assigneeCacheService = new AssigneeCacheService();

export default assigneeCacheService;
